import React, { FC, useState } from "react";
import { Box, Checkbox, Drawer, Typography } from "@mui/material";
import Button from "@mui/material/Button";
import useApi from "../../../../hooks/useApi";
import { useUser } from "../../../../context/UserContext";
import { submitPostIf } from "../../../../services/pifService";
import { POSTIF_RESULT_CODE } from "../../../../services/pifWriteResult";
import { PostIfResultDto } from "../../../../types/pifDto";
import { getEstDateBySameDayFormat } from "../../../../utils/dateHelper";
import { PifFormValues, PifGap, PifSectionId, PifSummaryRow } from "../types";
import "./PifReviewDrawer.scss";

/**
 * Turns a refused submit into the heading and the lines listed under it.
 *
 * `missing` (-3) names the required fields with no answer; `rejected` (-2) explains why a value was
 * refused - today only the requested rate, against the position's hiring band. No other code
 * reaches here: readPostIfWrite throws those as a sentence for the central alert.
 */
const describeSubmitRefusal = (result: PostIfResultDto): { heading: string; items: string[] } => {
  const missing = result.missing ?? [];
  if (missing.length) {
    return {
      heading: `The form couldn't be submitted - ${missing.length} required ${
        missing.length === 1 ? "answer is" : "answers are"
      } still missing`,
      items: missing,
    };
  }

  const rejected = (result.rejected ?? [])
    .map((row) => [row.fieldKey, row.reason].filter(Boolean).join(" - "))
    .filter(Boolean);
  return {
    heading: rejected.length
      ? "The form couldn't be submitted - an answer was refused"
      : "The form couldn't be submitted.",
    items: rejected,
  };
};

interface PifReviewDrawerProps {
  open: boolean;
  gaps: PifGap[];
  values: PifFormValues;
  /**
   * Flushes the form to the API and answers the id it saved against - null if the save failed.
   * Submit reads the database, not our state, so this runs first. Since Bug 79824 it is also what
   * CREATES the record, so the id cannot be known before it runs and must come back from it.
   */
  onPersist: () => Promise<number | null>;
  attested: boolean;
  onToggleAttest: () => void;
  onClose: () => void;
  /** Closes the drawer and takes the user to the gap — `field` focuses the control that answers it. */
  onJump: (id: PifSectionId, field?: string) => void;
  onSigned: (refNo: string, signedAt: string) => void;
}

const PifReviewDrawer: FC<PifReviewDrawerProps> = ({
  open,
  gaps,
  values: f,
  onPersist,
  attested,
  onToggleAttest,
  onClose,
  onJump,
  onSigned,
}) => {
  const { executeApi } = useApi();
  const { firstName, lastName, userName } = useUser();
  const [signing, setSigning] = useState(false);
  const [problem, setProblem] = useState<{ heading: string; items: string[] } | null>(null);

  const signerName = `${firstName ?? ""} ${lastName ?? ""}`.trim() || userName || "—";

  const summary: PifSummaryRow[] = [
    { k: "Applicant", v: `${f.firstName} ${f.lastName}`.trim() || "—" },
    { k: "Kind of hire", v: f.hireType || "—" },
    { k: "Position", v: f.pos || "—" },
    { k: "Department", v: f.dept || "—" },
    { k: "Schedule", v: `${f.empType}${f.hours ? ` · ${f.hours} hrs/week` : ""}` },
    { k: "Rate", v: f.rate ? `$${f.rate} ${(f.rateType || "").toLowerCase()}`.trim() : "—" },
    { k: "Starts", v: f.startDate || "—" },
    { k: "Reports to", v: f.reportsTo || "—" },
    { k: "Signed by", v: signerName },
  ];

  const nowStamp = new Date().toLocaleDateString("en-US", { month: "short", day: "numeric" });
  // No !postIfId clause: the record is created BY the save this button triggers (Bug 79824), so
  // requiring one up front left "Sign & submit" permanently disabled on a complete form.
  const signDisabled = gaps.length > 0 || !attested || signing;

  /**
   * Save, then submit — in that order, and never submit if the save failed.
   *
   * sProc_Portal_PostIF_Submit_U checks the DATABASE for every required EAV answer and for a
   * linked applicant row; it cannot see React state. Submitting without flushing first came
   * back -3 with the whole form listed as missing even though it was filled on screen.
   *
   * Submit is also the API's own completeness check, and its `missing` list can differ from
   * ours when the field catalogue changed after this draft was opened — so show the server's.
   */
  const handleSign = async () => {
    setSigning(true);
    setProblem(null);

    const postIfId = await onPersist();
    if (!postIfId) {
      setSigning(false);
      return;
    }

    const result = await executeApi<number, PostIfResultDto>(submitPostIf, postIfId);
    setSigning(false);
    if (!result) return;

    // -2 and -3 RESOLVE rather than throw (readPostIfWrite hands back their per-field payload),
    // so the CODE - not merely the presence of a list - is what separates a signed form from a
    // refused one. Reading only `missing` would treat a rate-band refusal (-2) as a signature.
    if (result.code !== POSTIF_RESULT_CODE.SUCCESS) {
      setProblem(describeSubmitRefusal(result));
      return;
    }
    onSigned(result.refNo ?? "", getEstDateBySameDayFormat(new Date()));
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{ className: "pif-review-drawer" }}
      slotProps={{ backdrop: { className: "pif-review-backdrop" } }}
    >
      <Box className="pif-review-drawer__root">
        <Box className="pif-review-drawer__header">
          <Box>
            <Typography className="pif-review-drawer__title">{gaps.length ? "Almost there" : "Review & sign"}</Typography>
            <Typography className="pif-review-drawer__subtitle">
              {gaps.length ? `${gaps.length} answers still needed` : "Check it once, then sign"}
            </Typography>
          </Box>
          <Button variant="outlined" size="small" onClick={onClose} className="pif-review-drawer__close">
            Close
          </Button>
        </Box>

        <Box className="pif-review-drawer__body">
          {gaps.length > 0 && (
            <Box className="pif-review-drawer__gaps">
              <Typography className="pif-review-drawer__gaps-title">
                {gaps.length} {gaps.length === 1 ? "answer is" : "answers are"} still missing
              </Typography>
              {gaps.map((g) => (
                <Box
                  key={g.label}
                  onClick={() => onJump(g.sec, g.field)}
                  className="pif-review-drawer__gap-row"
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      onJump(g.sec, g.field);
                    }
                  }}
                >
                  <Typography className="pif-review-drawer__gap-label">{g.label}</Typography>
                  <Typography className="pif-review-drawer__gap-jump">Jump →</Typography>
                </Box>
              ))}
            </Box>
          )}

          <Box className="pif-review-drawer__summary">
            {summary.map((row) => (
              <Box key={row.k} className="pif-review-drawer__summary-row">
                <Typography className="pif-review-drawer__summary-key">{row.k}</Typography>
                <Typography className="pif-review-drawer__summary-val">{row.v}</Typography>
              </Box>
            ))}
          </Box>

          <Box className="pif-review-drawer__attest-note">
            Attesting is your signature. We record your name, the date and time, and your IP address — no scribble
            needed. The form then locks and moves to the Department Director.
          </Box>

          {problem && (
            <Box className="pif-review-drawer__gaps" data-testid="postif-submit-missing">
              <Typography className="pif-review-drawer__gaps-title">{problem.heading}</Typography>
              {problem.items.map((label) => (
                <Box key={label} className="pif-review-drawer__gap-row">
                  <Typography className="pif-review-drawer__gap-label">{label}</Typography>
                </Box>
              ))}
            </Box>
          )}

          <Box className="pif-review-drawer__signature">
            <Typography className="pif-review-drawer__signature-name">{signerName}</Typography>
            <Box className="pif-review-drawer__signature-line" />
            <Typography className="pif-review-drawer__signature-meta">Hiring supervisor · {nowStamp}</Typography>
          </Box>

        </Box>

        {/* Outside the scrolling body, with the button it unlocks: as the last child of the body it
            sat below the summary and the signature block — and a long missing-answers list pushed
            it further — so the tick you have to make was off screen until you scrolled. */}
        <Box
          component="label"
          className={`pif-review-drawer__attest${attested ? " pif-review-drawer__attest--checked" : ""}`}
        >
          <Checkbox checked={attested} onChange={onToggleAttest} data-testid="postif-attest-confirm" />
          <Typography className="pif-review-drawer__attest-text">
            I confirm the details above are accurate, that this hire has HR Director approval, and I understand the
            form locks once signed.
          </Typography>
        </Box>

        <Box className="pif-review-drawer__footer">
          <Typography className="pif-review-drawer__footer-hint">
            {gaps.length
              ? "Fill the missing answers first."
              : !attested
                ? "Tick the confirmation to enable signing."
                : "This saves the form, then locks it."}
          </Typography>
          <Button variant="contained" disabled={signDisabled} onClick={handleSign} data-testid="postif-sign-submit">
            {signing ? "Submitting…" : "Sign & submit"}
          </Button>
        </Box>
      </Box>
    </Drawer>
  );
};

export default PifReviewDrawer;
